import { useMemo } from 'react'
import useApiService from 'services/api'
import { getTargetType } from './targetTypeMap'
import { adaptUnifiedListParams } from './unifiedResponseAdapter'
import {
  adaptUnifiedDoorItemForVertical,
  DoorVerticalRowConfig,
} from './unifiedDoorResponseAdapter'

/**
 * Unified data-source actions for an opportunity-DOORS target table (FIR-6995,
 * default-on since the FIR-6997 cutover).
 *
 * The door mirror of `useUnifiedTableActions`. Returns `{ listAction,
 * summaryAction }` bound to a door-bearing vertical. The actions hit the
 * unified `/opportunities/doors/` endpoint (resolving the vertical through
 * `getTargetType`, shaping outgoing params via `adaptUnifiedListParams`, and
 * re-nesting each row via `adaptUnifiedDoorItemForVertical` so the
 * per-vertical door columns/links/actions resolve).
 *
 * Door-summary mapping: the door stat card / select-all total reads
 * `summaryRes.alt_count` (NOT `count`). `alt_count` = distinct parent-chain
 * count (distinct `contact_company_id`, the chain-proxy FK), while `count` =
 * DOOR count — the same semantics the per-vertical door summaries had, so this
 * adapter passes them through unchanged.
 *
 * @param vertical app vertical / sub-category slug (routed through `getTargetType`)
 * @param rowConfig how to re-nest the flat unified door row for this vertical
 */
export function useUnifiedDoorsTableActions<T extends { id: number }>(
  vertical: string,
  rowConfig: DoorVerticalRowConfig
): {
  listAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ results: T[] }>
  summaryAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ count: number; alt_count: number }>
} {
  const api = useApiService()

  return useMemo(() => {
    const targetType = getTargetType(vertical)

    const listAction = async (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => {
      const res = await api.getOpportunityDoorsList(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      return {
        ...res,
        results: res.results.map((item) =>
          adaptUnifiedDoorItemForVertical(
            item as Record<string, unknown>,
            rowConfig
          )
        ),
      } as unknown as { results: T[] }
    }

    const summaryAction = async (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => {
      const res = await api.getOpportunityDoorsSummary(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      // `count` = door count, `alt_count` = distinct parent-chain count (the
      // door stat-card total). Pass both through.
      return { count: res.count, alt_count: res.alt_count }
    }

    return { listAction, summaryAction }
    // Keyed on the vertical + row config; `rowConfig` object identity is not
    // stable at call sites, so depend on its scalar fields instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vertical, api, rowConfig.accessor, rowConfig.detailLinkIdField])
}
